/** Authentication states for Program and Custom Program intakes. */
export const AUTH_STATE = Object.freeze({
  unidentified: "UNIDENTIFIED",
  lookupPending: "LOOKUP_PENDING",
  lookupRetry: "LOOKUP_RETRY",
  inactiveAccount: "INACTIVE_ACCOUNT",
  existingNonPatient: "EXISTING_NON_PATIENT",
  existingCredentials: "EXISTING_CREDENTIALS",
  passwordReset: "PASSWORD_RESET",
  newRegistration: "NEW_REGISTRATION",
  authenticated: "AUTHENTICATED",
  intakeReady: "INTAKE_READY",
  eligibilityBlocked: "ELIGIBILITY_BLOCKED",
  reset: "RESET",
});

export const AUTH_EVENT = Object.freeze({
  submitEmail: "SUBMIT_EMAIL",
  submitCredentials: "SUBMIT_CREDENTIALS",
  submitRegistration: "SUBMIT_REGISTRATION",
  submitProfile: "SUBMIT_PROFILE",
  lookupFound: "LOOKUP_FOUND",
  lookupNotFound: "LOOKUP_NOT_FOUND",
  lookupInactive: "LOOKUP_INACTIVE",
  lookupNonPatient: "LOOKUP_NON_PATIENT",
  lookupUnavailable: "LOOKUP_UNAVAILABLE",
  retry: "RETRY",
  changeEmail: "CHANGE_EMAIL",
  forgotPassword: "FORGOT_PASSWORD",
  backToLogin: "BACK_TO_LOGIN",
  credentialsAccepted: "CREDENTIALS_ACCEPTED",
  credentialsRejected: "CREDENTIALS_REJECTED",
  accountCreated: "ACCOUNT_CREATED",
  identityExists: "IDENTITY_EXISTS",
  validationFailed: "VALIDATION_FAILED",
  bindSucceeded: "BIND_SUCCEEDED",
  bindDisqualified: "BIND_DISQUALIFIED",
  sessionExpired: "SESSION_EXPIRED",
  resetIdentity: "RESET_IDENTITY",
});

/** Outcomes returned when the authenticated Patient is bound to the runtime. */
export const AUTH_BIND_DISPOSITION = Object.freeze({
  ready: "ready",
  profileIncomplete: "profile_incomplete",
  careLocationRequired: "care_location_required",
  disqualified: "disqualified",
});

/** Stable intake error codes mirrored from `apps/treatments/intake_errors.py`. */
export const AUTH_ERROR = Object.freeze({
  emailLookupUnavailable: "email_lookup_unavailable",
  authenticationRequired: "patient_authentication_required",
  credentialsInvalid: "patient_credentials_invalid",
  authenticationUnavailable: "patient_authentication_unavailable",
  rateLimited: "patient_authentication_rate_limited",
  profileIncomplete: "patient_profile_incomplete",
  identityConflict: "patient_identity_conflict",
  accountCreateUnavailable: "account_create_unavailable",
  inactiveAccount: "inactive_account",
  nonPatientAccount: "non_patient_account",
  passwordResetCodeInvalid: "password_reset_code_invalid",
  passwordResetUnavailable: "password_reset_unavailable",
});

export const AUTH_ACCOUNT_STATUS = Object.freeze({
  new: "new",
  activePatient: "active_patient",
  inactive: "inactive",
  nonPatient: "non_patient",
});

/** Patient-facing copy. Never render a raw error code or identifier. */
export const AUTH_COPY = Object.freeze({
  emailStep: {
    eyebrowPrefix: "Welcome to",
    title: "Let's get started",
    description:
      "Enter your email and we'll check if you already have an account with us.",
    emailLabel: "Email Address",
    emailPlaceholder: "your.email@example.com",
    consentNotePrefix: "By continuing you agree to receive emails from",
    consentNoteSuffix: "about your care.",
    submit: "Continue",
    submitting: "Checking…",
  },
  loginStep: {
    eyebrow: "Sign in to your account",
    title: "Welcome back",
    description:
      "Enter your password to continue. We'll skip questions you've already answered.",
    emailLabel: "Email Address",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter your password",
    submit: "Log In",
    submitting: "Signing in…",
    changeEmailPrompt: "New here?",
    changeEmail: "Create account",
    forgotPassword: "Forgot password?",
  },
  passwordReset: {
    eyebrow: "Reset your password",
    backToLogin: "Back to sign in",
    requestStep: {
      title: "Forgot your password?",
      description: "We'll email you a 6-digit code to reset your password.",
      emailLabel: "Email Address",
      submit: "Send code",
      submitting: "Sending…",
    },
    codeStep: {
      title: "Check your email",
      descriptionPrefix: "Enter the 6-digit code we sent to",
      descriptionSuffix: "It expires in 10 minutes.",
      codeLabel: "Code",
      codePlaceholder: "6-digit code",
      submit: "Continue",
      submitting: "Checking…",
      resend: "Send a new code",
      resent: "We sent you a new code.",
    },
    passwordStep: {
      title: "Choose a new password",
      newPasswordLabel: "New password",
      newPasswordPlaceholder: "At least 8 characters",
      confirmPasswordLabel: "Confirm new password",
      confirmPasswordPlaceholder: "Re-enter your new password",
      submit: "Save and continue",
      submitting: "Saving…",
    },
  },
  registrationStep: {
    eyebrow: "Account setup",
    title: "Create your account",
    firstNameLabel: "First Name",
    lastNameLabel: "Last Name",
    dobLabel: "Date of Birth",
    dobHelp: "You must be 18 or older to create an account.",
    sexLabel: "Sex assigned at birth",
    sexHelp:
      "Some treatments are sex-specific. We use this to determine which care plans are right for you.",
    emailLabel: "Email Address",
    phoneLabel: "Phone Number",
    submit: "Create Account",
    submitting: "Creating your account…",
    existingPrompt: "Already have an account?",
    existingAction: "Log in",
  },
  missingProfileStep: {
    eyebrow: "One more thing",
    title: "Complete your profile",
    description: "We need a little more information before you continue.",
    submit: "Continue",
    submitting: "Saving…",
  },
  errors: {
    emailRequired: "Enter your email address.",
    emailInvalid: "Enter a valid email address.",
    passwordRequired: "Enter your password.",
    lookupUnavailable:
      "We could not check your email just now. Please try again.",
    retry: "Try again",
    authenticationUnavailable:
      "We could not sign you in just now. Please try again.",
    accountCreateUnavailable:
      "We could not create your account just now. Please try again.",
    inactiveAccount:
      "This email is already associated with an inactive account. Please contact support or use another email address.",
    nonPatientAccount:
      "This email is already associated with an existing account. Please contact support or use another email address.",
    identityConflict:
      "This phone number is already associated with an existing patient. Please use a different phone number.",
    resetCodeRequired: "Enter the 6-digit code from your email.",
    resetCodeInvalid:
      "That code is not valid or has expired. Check the code or request a new one.",
    resetCodeUnavailable:
      "We could not send a code just now. Please try again.",
    resetUnavailable:
      "We could not reset your password just now. Please try again.",
    newPasswordTooShort: "Use at least 8 characters.",
    newPasswordMismatch: "The passwords do not match.",
  },
});

export const SEX_OPTIONS = Object.freeze([
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
]);

export const REQUIRED_ACKNOWLEDGEMENTS = Object.freeze([
  "terms_accepted",
  "privacy_accepted",
  "telehealth_consent_accepted",
]);

export const MINIMUM_REGISTRATION_AGE = 18;
export const MINIMUM_PASSWORD_LENGTH = 8;
export const MAXIMUM_PASSWORD_LENGTH = 128;
export const MAXIMUM_EMAIL_LENGTH = 255;
export const MAXIMUM_NAME_LENGTH = 100;

/** Field labels keyed by backend profile field name. */
export const PROFILE_FIELD_LABEL = Object.freeze({
  date_of_birth: "Date of Birth",
  sex: "Sex assigned at birth",
});
